import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const SERVICE_DETAILS = {
  basic: {
    title: '🧼 BASIC CLEANING',
    intro: [
      'Routine cleaning to keep your home fresh, tidy, and well-maintained.',
      'Designed for regularly cleaned homes and ongoing maintenance.'
    ],
    includesTitle: '✅ Basic Cleaning — What\'s Included',
    total: 'Total: 48 individual cleaning tasks',
    sections: [
      {
        title: '🧑‍🍳 Kitchen (12 tasks)',
        items: [
          'Sanitize sink and scrub surfaces',
          'Clean countertops & stovetop',
          'Wipe exterior of major appliances (oven, fridge, dishwasher)',
          'Clean microwave (inside & out)',
          'Wipe cabinet doors',
          'Clean tables & chairs',
          'Wipe backsplashes',
          'Clean small appliances (toaster, coffee maker)',
          'Dust and clean light fixtures',
          'Clean window sills',
          'Vacuum and mop floors',
          'Empty trash'
        ]
      },
      {
        title: '🚿 Bathrooms (13 tasks)',
        items: [
          'Sanitize sinks, faucets & counters',
          'Disinfect toilet (inside & out)',
          'Scrub shower, tub & tiles',
          'Clean mirrors & glass',
          'Wipe cabinet doors & handles',
          'Clean bathroom fixtures & accessories',
          'Sanitize soap dispensers',
          'Wipe walls & light switches (as needed)',
          'Clean window sills & blinds',
          'Sweep, vacuum & mop floors',
          'Clean backsplashes & tiled areas',
          'Empty trash'
        ]
      },
      {
        title: '🛏️ Bedrooms (10 tasks)',
        items: [
          'Make beds (change linens if provided)',
          'Dust furniture & surfaces',
          'Clean mirrors',
          'Tidy pillows & blankets',
          'Wipe nightstands & dressers',
          'Clean lamps',
          'Clean window sills',
          'Vacuum & mop floors',
          'Vacuum under beds (if accessible)',
          'Empty trash'
        ]
      },
      {
        title: '🛋️ Living Areas, Hallways & Stairs (9 tasks)',
        items: [
          'Dust furniture & shelves',
          'Clean glass surfaces',
          'Vacuum sofas & chairs (including cushions)',
          'Light organization for a tidy appearance',
          'Clean window sills',
          'Arrange pillows & cushions',
          'Dust railings, banisters & handrails',
          'Vacuum stairs & hallways',
          'Vacuum & mop floors'
        ]
      },
      {
        title: '🪟 Windows (4 tasks)',
        items: [
          'Kitchen sink window cleaned',
          'Patio door windows cleaned',
          'Interior windows (upon request)',
          'Window tracks vacuumed & wiped'
        ]
      }
    ],
    bestFor: [
      'Weekly, bi-weekly, or monthly service',
      'Homes without heavy buildup',
      'Ongoing maintenance cleaning'
    ],
    notIncluded: [
      'Inside oven or fridge',
      'Inside cabinets & drawers',
      'Laundry or dishwashing',
      'Pet hair removal',
      'Heavy buildup or specialty cleaning'
    ],
    price: '$0.08 / sq ft'
  },
  deep: {
    title: '🧽 DEEP CLEANING',
    intro: [
      'Intensive, detail-focused cleaning designed to remove buildup and restore freshness.',
      'Recommended for homes that haven\'t been professionally cleaned in a while or need a full reset.'
    ],
    includesTitle: '✅ Deep Cleaning — What\'s Included',
    total: 'Total: 62 individual cleaning tasks',
    note:
      '(Includes all Basic Cleaning tasks, performed more thoroughly, plus additional detailed work listed below.)',
    sections: [
      {
        title: '🧑‍🍳 Kitchen (16 tasks)',
        items: [
          'Deep scrub and sanitize sink and surrounding areas',
          'Scrub countertops & stovetop to remove buildup',
          'Wipe exterior of major appliances (oven, fridge, dishwasher)',
          'Clean microwave (inside & out)',
          'Wipe cabinet doors, handles & edges',
          'Clean tables & chairs',
          'Scrub backsplashes',
          'Clean small appliances (toaster, coffee maker)',
          'Detail light fixtures',
          'Clean window sills',
          'Detail baseboards (reachable areas)',
          'Wipe door frames & trim',
          'Clean switches & high-touch areas',
          'Vacuum floors',
          'Mop floors with extra attention to edges',
          'Empty trash'
        ]
      },
      {
        title: '🚿 Bathrooms (18 tasks)',
        items: [
          'Deep sanitize sinks, faucets & counters',
          'Disinfect toilet (inside & out)',
          'Scrub shower, tub & tiles to remove soap scum',
          'Detail grout lines & edges',
          'Clean mirrors & glass',
          'Wipe cabinet doors & handles',
          'Clean bathroom fixtures & accessories',
          'Sanitize soap dishes & dispensers',
          'Wipe door frames & trim',
          'Clean switches & high-touch areas',
          'Wipe walls (as needed)',
          'Clean window sills & blinds',
          'Sweep floors',
          'Vacuum floors',
          'Mop floors thoroughly',
          'Clean backsplashes & tiled areas',
          'Detail baseboards (reachable areas)',
          'Empty trash'
        ]
      },
      {
        title: '🛏️ Bedrooms (13 tasks)',
        items: [
          'Make beds (change linens if provided)',
          'Dust furniture & surfaces in detail',
          'Clean mirrors',
          'Tidy pillows & blankets',
          'Wipe nightstands & dressers',
          'Clean lamps & fixtures',
          'Clean window sills',
          'Detail baseboards (reachable areas)',
          'Wipe door frames & trim',
          'Vacuum floors',
          'Mop floors (if applicable)',
          'Vacuum under beds & furniture (if accessible)',
          'Empty trash'
        ]
      },
      {
        title: '🛋️ Living Areas, Hallways & Stairs (11 tasks)',
        items: [
          'Dust furniture & shelves in detail',
          'Clean glass surfaces',
          'Vacuum sofas & chairs (including cushions)',
          'Light organization for a tidy appearance',
          'Clean window sills',
          'Arrange pillows & cushions',
          'Dust railings, banisters & handrails',
          'Detail baseboards (reachable areas)',
          'Vacuum stairs & hallways',
          'Mop hard floors with edge detailing',
          'Clean high-touch areas (switches, handles)'
        ]
      },
      {
        title: '🪟 Windows (4 tasks)',
        items: [
          'Kitchen sink window cleaned',
          'Patio door windows cleaned',
          'Interior window glass & sills cleaned',
          'Window tracks vacuumed & wiped'
        ]
      }
    ],
    bestFor: [
      'First-time or occasional cleaning',
      'Homes with visible buildup',
      'After guests or pets',
      'Seasonal deep refresh'
    ],
    notIncluded: [
      'Inside oven or fridge',
      'Inside cabinets & drawers',
      'Laundry or dishwashing',
      'Heavy pet hair or extreme buildup'
    ],
    price: '$0.15 / sq ft'
  }
};

export default function ServiceDetailsModal({ serviceId, onClose }) {
  const content = SERVICE_DETAILS[serviceId];

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!content) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [content, handleKeyDown]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!content) return null;

  return createPortal(
    <div
      className="legal-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={content.title}
      onClick={handleOverlayClick}
    >
      <div className="legal-modal service-modal">
        <header className="legal-modal__header">
          <button type="button" className="legal-modal__close" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="legal-modal__body service-modal__body">
          <div className="service-modal__content">
            <div>
              <h2 className="service-modal__title">{content.title}</h2>
              {content.intro.map((line, index) => (
                <p key={index} className="service-modal__intro">
                  {line}
                </p>
              ))}
            </div>

            <div>
              <h3 className="service-modal__section-title">{content.includesTitle}</h3>
              <p className="service-modal__meta">{content.total}</p>
              {content.note && <p className="service-modal__note">{content.note}</p>}
            </div>

            {content.sections.map((section) => (
              <div key={section.title} className="service-modal__section">
                <h4 className="service-modal__section-heading">{section.title}</h4>
                <ul className="service-modal__list">
                  {section.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="service-modal__section">
              <h4 className="service-modal__section-heading">Best for:</h4>
              <ul className="service-modal__list">
                {content.bestFor.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="service-modal__section">
              <h4 className="service-modal__section-heading">Not included (available as add-ons):</h4>
              <ul className="service-modal__list">
                {content.notIncluded.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <p className="service-modal__price">Price: {content.price}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
